import { type FlightSummary, type TravelDirection, TRAVEL_SERVICE_NO_MAX, formatFlightDesignator, parseFlightDesignator } from "@seagames/contracts";
import { Icons } from "@seagames/ui";
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDayShort, wallTimeIn } from "../../lib/format/datetime";
import { DatePickerCalendar } from "../common/date-picker-calendar";
import { F, Pair, TextInput, inputClass } from "../accreditation/register/parts";
import { ConnectionRiskChip, FlightCard } from "./flight-status";
import { useFlightDates, useFlightLookup } from "./hooks";
import { type ConnectionDraft, airDateMode, designatorKey, junctionOf, splitMinutes } from "./model";

/**
 * Transit (user, 2026-10-15): the parts of the leg form that show a leg's
 * whole chain of flights — one row per connecting
 * flight, and the change between two flights ("+ Add a connecting flight"
 * under the found flight starts it). The main flight keeps its own
 * block in `TravelLegForm`; these only wrap around it.
 */

/**
 * One step of the chain on its rail: a dot, the flight's label ("1st flight"),
 * an action at the right, then its fields. `last` ends the rail.
 */
export function ChainStep({ label, action, last = false, children }: { label: ReactNode; action?: ReactNode; last?: boolean; children: ReactNode }) {
	return (
		<section className="relative grid min-w-0 gap-[0.75rem] pl-[1.375rem]">
			<span aria-hidden className="absolute top-[0.3125rem] left-0 size-[0.6875rem] rounded-full border-2 border-gms-navy bg-base-100" />
			{!last && <span aria-hidden className="absolute top-[1.125rem] -bottom-[0.875rem] left-[0.3125rem] w-px bg-gms-line-2" />}
			<div className="flex min-w-0 items-baseline justify-between gap-[0.75rem]">
				<h4 className="text-[0.8125rem] leading-[1.3] font-semibold text-gms-navy-ink">{label}</h4>
				{action}
			</div>
			{children}
		</section>
	);
}

/** On the rail between two steps: an "Add another connection" button or the change between two flights. `end`: the rail stops here. */
export function ChainGap({ children, end = false }: { children: ReactNode; end?: boolean }) {
	return (
		<div className="relative min-w-0 pl-[1.375rem]">
			<span aria-hidden className={`absolute -top-[0.875rem] left-[0.3125rem] w-px bg-gms-line-2 ${end ? "bottom-1/2" : "-bottom-[0.875rem]"}`} />
			{children}
		</div>
	);
}

/** A row's text button (Add another connection, Remove) in the form's link style. */
export function LinkButton({ onClick, children, label, disabled }: { onClick: () => void; children: ReactNode; label?: string; disabled?: boolean }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			disabled={disabled}
			className="inline-flex shrink-0 cursor-pointer items-center gap-[0.25rem] rounded-[0.25rem] text-[0.8125rem] font-semibold text-gms-navy hover:underline hover:underline-offset-[3px] focus-visible:outline-2 focus-visible:outline-gms-navy disabled:cursor-not-allowed disabled:opacity-50"
		>
			{children}
		</button>
	);
}

/**
 * The change between two consecutive flights, read off their cards:
 * "Change at Kota Kinabalu (BKI) · 2 h 05 min", amber with "Connection at
 * risk" under MIN_CONNECTION_MINUTES; red when the flights do not join (the
 * server would refuse the save) — unless the save already failed on it, then
 * the message under the flight says it (`quiet`).
 */
export function Junction({ prev, next, quiet = false }: { prev: FlightSummary | null; next: FlightSummary | null; quiet?: boolean }) {
	const { t } = useTranslation("travel");
	if (!prev || !next) return null;
	const j = junctionOf(prev, next);
	if (j.routeMismatch || j.timeOrder) {
		if (quiet) return null;
		return (
			<p role="status" className="flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-bad">
				<Icons.Alert className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
				<span className="min-w-0">{j.routeMismatch ? t("legForm.transit.routeMismatch", { lands: j.iata ?? "—", leaves: j.nextIata ?? "—" }) : t("legForm.transit.timeOrder")}</span>
			</p>
		);
	}
	const place = j.name && j.iata ? t("legForm.flight.place", { name: j.name, code: j.iata }) : (j.iata ?? t("legForm.pickMiss.unknownPlace"));
	const parts = j.minutes === null ? null : splitMinutes(j.minutes);
	const duration = parts === null ? null : parts.hours > 0 ? t("legForm.transit.duration", { hours: parts.hours, minutes: parts.minutes }) : t("legForm.transit.durationMinutes", { minutes: Number(parts.minutes) });
	return (
		<p className={`flex flex-wrap items-center gap-x-[0.5rem] gap-y-[0.25rem] text-[0.78125rem] leading-[1.4] ${j.tight ? "font-medium text-gms-warn" : "text-gms-ink-2"}`}>
			<svg viewBox="0 0 24 24" aria-hidden className="size-[0.9375rem] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
				<path d="M7 7h11l-3-3M17 17H6l3 3" />
			</svg>
			<span className="min-w-0 tabular-nums">{duration ? t("legForm.transit.change", { place, duration }) : t("legForm.transit.changeNoTime", { place })}</span>
			{j.tight && <ConnectionRiskChip />}
		</p>
	);
}

/** A flight's message under its block (a failed save, or a pre-save check): focusable by the chain key's DOM id. */
export function ChainError({ id, error }: { id: string; error: string | undefined }) {
	if (!error) return null;
	return (
		<p id={id} tabIndex={-1} role="alert" className="flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-bad outline-none">
			<Icons.Alert className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
			<span className="min-w-0">{error}</span>
		</p>
	);
}

type PickProblem = { key: string; day: string; reason: "NOT_FOUND" | "UNAVAILABLE" | "RATE_LIMITED" | "TRACKING_OFF" };

/**
 * One connecting flight: its number (+ search), the day it flies picked from
 * the days it operates (`GET /flights/dates`), and its card once looked up
 * (`role=CONNECTION`: no Games airport needed). Only a looked-up flight can
 * be stored, so there is no typed fallback here: when the schedules cannot
 * find it, the row says so (remove it, and note it in Remarks).
 */
export function ConnectionRow({
	direction,
	row,
	idBase,
	timeZone,
	startFrom,
	invalid,
	disabled,
	onChange,
}: {
	direction: TravelDirection;
	row: ConnectionDraft;
	/** `${idPrefix}-chain.N` — the number is `${idBase}-flight`, the date `${idBase}-date`. */
	idBase: string;
	timeZone: string;
	/** The calendar opens from here (the previous flight's landing day). */
	startFrom: string | null;
	invalid: boolean;
	disabled: boolean;
	onChange: (next: ConnectionDraft) => void;
}) {
	const { t } = useTranslation("travel");
	const { state: lookupState, lookup, reset } = useFlightLookup(direction, "CONNECTION");
	// A row seeded with a number (moved from the main flight) asks for its days at once.
	const dates = useFlightDates(direction, row.service_no, row.service_no.trim() && !row.flight ? row.service_no : null);
	const [problem, setProblem] = useState<PickProblem | null>(null);
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [calendarFocus, setCalendarFocus] = useState(false);
	const [picking, setPicking] = useState<string | null>(null);
	const pickSeq = useRef(0);
	const dateButton = useRef<HTMLButtonElement>(null);
	const latest = useRef(row);
	latest.current = row;

	const key = designatorKey(row.service_no);
	const shownProblem = problem && problem.key === key ? problem : null;
	const mode = airDateMode(row, { committed: dates.committed, dates: dates.outcome, manual: null });
	const pickedDay = picking ?? row.flight?.service_date ?? null;
	const flightText = (() => {
		const p = parseFlightDesignator(row.service_no.trim());
		return p ? formatFlightDesignator(p.carrier_code, p.flight_number) : row.service_no.trim();
	})();
	const today = wallTimeIn(new Date().toISOString(), timeZone)?.date ?? new Date().toISOString().slice(0, 10);
	// The days arrived with nothing picked yet: show them without being asked.
	const offerCalendar = mode.kind === "picker" && !row.flight && !picking;
	useEffect(() => {
		if (offerCalendar) {
			setCalendarFocus(false);
			setCalendarOpen(true);
		}
	}, [offerCalendar]);
	const showCalendar = calendarOpen && mode.kind === "picker" && !disabled;
	const looking = mode.kind === "loading" || lookupState.kind === "loading";

	const commit = () => {
		if (disabled) return;
		dates.commit(row.service_no);
	};
	const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") return;
		e.preventDefault(); // look it up, never submit the sheet
		commit();
	};

	const pickDay = async (day: string) => {
		setCalendarOpen(false);
		dateButton.current?.focus();
		if (day === row.flight?.service_date) return;
		const seq = ++pickSeq.current;
		setPicking(day);
		const typed = row.service_no;
		const answer = await lookup(typed, day, "SERVICE");
		if (seq !== pickSeq.current) return;
		setPicking(null);
		if (!answer || latest.current.service_no !== typed) return;
		const res = answer.result;
		if (res?.found && res.flight) {
			setProblem(null);
			onChange({ ...latest.current, flight: res.flight });
			return;
		}
		const reason: PickProblem["reason"] = !res
			? answer.state.kind === "miss" && answer.state.reason === "RATE_LIMITED"
				? "RATE_LIMITED"
				: "UNAVAILABLE"
			: res.reason === "TRACKING_OFF"
				? "TRACKING_OFF"
				: "NOT_FOUND";
		setProblem({ key: designatorKey(typed), day, reason });
		if (latest.current.flight) onChange({ ...latest.current, flight: null });
		setCalendarFocus(false);
		setCalendarOpen(true);
	};

	const note = (() => {
		if (picking) return null;
		if (shownProblem) {
			switch (shownProblem.reason) {
				case "NOT_FOUND":
					return t("legForm.pickMiss.notFound", { flight: flightText, date: formatDayShort(shownProblem.day) });
				case "TRACKING_OFF":
					return t("legForm.transit.miss.TRACKING_OFF");
				case "RATE_LIMITED":
					return t("legForm.pickMiss.rateLimited");
				case "UNAVAILABLE":
					return t("legForm.pickMiss.unavailable");
			}
		}
		if (mode.kind === "typed") {
			if (mode.reason === "UNREADABLE") return t("legForm.transit.miss.UNREADABLE");
			if (mode.reason === "TRACKING_OFF") return t("legForm.transit.miss.TRACKING_OFF");
			if (mode.reason === "UNAVAILABLE") return t("legForm.transit.miss.UNAVAILABLE");
			if (mode.reason === "RATE_LIMITED") return t("legForm.pickMiss.rateLimited");
			return t("legForm.transit.miss.NOT_FOUND", { flight: flightText });
		}
		if (mode.kind === "fixed") return t("legForm.dates.fixed");
		return null;
	})();
	const quietNote = mode.kind === "await" ? t("legForm.transit.prompt") : mode.kind === "picker" && !row.flight && !picking && !shownProblem ? t("legForm.dates.pickPrompt") : null;

	const dateText = pickedDay ? formatDayShort(pickedDay) : mode.kind === "loading" ? t("legForm.dates.loading") : mode.kind === "picker" ? t("legForm.dates.pick") : t("legForm.dates.awaitFlight");
	const dateClickable = mode.kind === "picker" || mode.kind === "attached";

	return (
		<div className="grid min-w-0 gap-[0.75rem]">
			<Pair>
				<F label={t("legForm.field.flight")} htmlFor={`${idBase}-flight`} required>
					<div className="relative min-w-0">
						<TextInput
							id={`${idBase}-flight`}
							value={row.service_no}
							invalid={invalid}
							maxLength={TRAVEL_SERVICE_NO_MAX}
							autoComplete="off"
							spellCheck={false}
							placeholder={t("legForm.placeholder.flight")}
							className="pr-[2.75rem]"
							onChange={(e) => {
								reset();
								pickSeq.current++;
								setPicking(null);
								setProblem(null);
								setCalendarOpen(false);
								onChange({ ...row, service_no: e.target.value.toUpperCase(), flight: null });
							}}
							onBlur={commit}
							onKeyDown={onKey}
						/>
						<button
							type="button"
							aria-label={t("legForm.lookup.button")}
							title={t("legForm.lookup.button")}
							disabled={disabled || !row.service_no.trim()}
							aria-busy={looking || undefined}
							onMouseDown={(e) => e.preventDefault()}
							onClick={commit}
							className="absolute inset-y-0 right-[0.375rem] my-auto inline-flex size-[2.25rem] cursor-pointer items-center justify-center rounded-[0.375rem] text-gms-ink-3 transition-colors hover:bg-gms-soft hover:text-gms-navy focus-visible:outline-2 focus-visible:outline-gms-navy disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gms-ink-3"
						>
							{looking ? <span className="loading loading-spinner size-[0.9375rem]" aria-hidden /> : <Icons.Search className="size-[1.0625rem]" />}
						</button>
					</div>
				</F>
				<F label={t("legForm.field.flightDateDeparts")} htmlFor={`${idBase}-date`} required>
					<button
						ref={dateButton}
						id={`${idBase}-date`}
						type="button"
						disabled={!dateClickable && !pickedDay}
						aria-disabled={!dateClickable || undefined}
						aria-expanded={dateClickable ? showCalendar : undefined}
						aria-controls={dateClickable ? `${idBase}-calendar` : undefined}
						onClick={
							dateClickable
								? () => {
										if (mode.kind === "attached") commit(); // the days are fetched only now
										setCalendarFocus(true);
										setCalendarOpen((o) => !o);
									}
								: undefined
						}
						className={inputClass(
							false,
							`flex items-center justify-between gap-[0.5rem] text-left ${dateClickable ? "cursor-pointer" : "cursor-default bg-gms-soft"} disabled:cursor-not-allowed disabled:bg-gms-soft disabled:text-gms-ink-3`,
						)}
					>
						<span className={`truncate ${pickedDay ? "font-medium text-gms-navy-ink" : "text-[#9a9cb2]"}`}>{dateText}</span>
						{mode.kind === "loading" || (picking && lookupState.kind === "loading") ? (
							<span className="loading loading-spinner size-[0.875rem] text-gms-ink-3" aria-hidden />
						) : (
							<svg viewBox="0 0 24 24" aria-hidden className="size-[1.0625rem] shrink-0 text-gms-ink-3" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
								<rect x="3.5" y="5" width="17" height="15" rx="2" />
								<path d="M3.5 10h17M8 3v4M16 3v4" />
							</svg>
						)}
					</button>
				</F>
			</Pair>
			{showCalendar && mode.kind === "picker" && (
				<div className="-mt-[0.25rem] grid justify-items-center gap-[0.5rem] rounded-field border border-gms-line bg-base-100 px-[0.75rem] pt-[0.625rem] pb-[0.75rem]">
					<DatePickerCalendar
						id={`${idBase}-calendar`}
						value={pickedDay}
						enabled={mode.dates}
						today={today}
						startFrom={startFrom}
						autoFocus={calendarFocus}
						label={t("legForm.dates.calendarLabel", { flight: row.service_no.trim() })}
						onSelect={(d) => void pickDay(d)}
						onEscape={() => {
							setCalendarOpen(false);
							dateButton.current?.focus();
						}}
					/>
					<p className="text-center text-[0.75rem] leading-[1.4] text-gms-ink-3">{t("legForm.dates.legend", { flight: row.service_no.trim() })}</p>
				</div>
			)}
			{row.flight ? (
				<FlightCard direction={direction} flight={row.flight} timeZone={timeZone} />
			) : note ? (
				<p role="status" className="-mt-[0.375rem] flex items-start gap-[0.3125rem] text-[0.78125rem] leading-[1.4] font-medium text-gms-warn">
					<Icons.Alert className="mt-[0.0625rem] size-[0.9375rem] shrink-0" />
					<span className="min-w-0">{note}</span>
				</p>
			) : quietNote ? (
				<p className="-mt-[0.375rem] text-[0.78125rem] leading-[1.4] text-gms-ink-3">{quietNote}</p>
			) : null}
		</div>
	);
}
